/**
 * Provider Registry
 * Manages AI provider registration and selection
 */

import { AIProvider, ProviderRegistry as IProviderRegistry, AIError, AIErrorType } from './types';

export class ProviderRegistry implements IProviderRegistry {
  private providers: Map<string, AIProvider> = new Map();
  private defaultProviderId: string | null = null;

  /**
   * Register a new AI provider
   */
  register(provider: AIProvider): void {
    if (!provider.id) {
      throw new AIError(
        AIErrorType.CONFIGURATION_ERROR,
        'Provider must have an id'
      );
    }

    if (this.providers.has(provider.id)) {
      console.warn(`Provider ${provider.id} is already registered. Overwriting.`);
    }

    this.providers.set(provider.id, provider);

    // Set as default if it's the first provider
    if (this.providers.size === 1) {
      this.defaultProviderId = provider.id;
    }
  }

  /**
   * Unregister a provider
   */
  unregister(providerId: string): void {
    if (!this.providers.has(providerId)) {
      throw new AIError(
        AIErrorType.PROVIDER_NOT_FOUND,
        `Provider ${providerId} not found`
      );
    }

    this.providers.delete(providerId);

    // Update default if the removed provider was default
    if (this.defaultProviderId === providerId) {
      this.defaultProviderId = this.providers.size > 0 
        ? this.providers.keys().next().value || null
        : null;
    }
  }

  /**
   * Get a provider by ID
   */
  get(providerId: string): AIProvider | undefined {
    return this.providers.get(providerId);
  }

  /**
   * List all registered providers
   */
  list(): AIProvider[] {
    return Array.from(this.providers.values());
  }

  /**
   * Get the default provider
   */
  getDefault(): AIProvider | undefined {
    if (!this.defaultProviderId) {
      return undefined;
    }
    return this.providers.get(this.defaultProviderId);
  }

  /**
   * Set the default provider
   */
  setDefault(providerId: string): void {
    if (!this.providers.has(providerId)) {
      throw new AIError(
        AIErrorType.PROVIDER_NOT_FOUND,
        `Cannot set default: Provider ${providerId} not found`
      );
    }
    this.defaultProviderId = providerId;
  }

  /**
   * Clear all providers
   */
  clear(): void {
    this.providers.clear();
    this.defaultProviderId = null;
  }

  /**
   * Get provider count
   */
  get size(): number {
    return this.providers.size;
  }

  /**
   * Check if a provider is registered
   */
  has(providerId: string): boolean {
    return this.providers.has(providerId);
  }
}

// Export singleton instance
export const providerRegistry = new ProviderRegistry();